import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip
} from 'chart.js'
import React, { useMemo } from 'react'
import { Bar } from 'react-chartjs-2'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

const formatDateByTimeframe = (
  stringDate: string,
  timeframe: 'hours' | 'days'
) => {
  switch (timeframe) {
    case 'hours':
      return `${stringDate}`
    case 'days':
      return stringDate
        .split('-')
        .map(datePart => datePart.padStart(2, '0'))
        .slice(0, 2)
        .join('/')
  }
}

interface IRevenueBarProps {
  revenues:
    | {
        date: string
        revenue: string
      }[]
    | {
        area: string | undefined
        revenue: { date: string; revenue: string }[]
      }[]
  timeframe: 'hours' | 'days'
  multiple?: boolean
}

const RevenueBar: React.FC<IRevenueBarProps> = ({
  revenues,
  timeframe,
  multiple
}) => {
  const options = {
    responsive: true,
    plugins: {
      legend: {
        display: multiple ?? false,
        position: 'bottom' as const,
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          boxWidth: 15,
          boxHeight: 15,
          padding: 32
        }
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: function (tooltipItem: any) {
            return 'Revenue: ' + tooltipItem?.raw?.toFixed(2)
          }
        }
      }
    },
    scales: {
      y: {
        grid: {
          color: '#edeaf1',
          drawTicks: false
        },
        border: {
          display: false
        },
        ticks: {
          color: '#524977',
          padding: 18,
          callback: function (value: string | number): string {
            const numericValue =
              typeof value === 'number' ? value : parseFloat(value)
            return new Intl.NumberFormat('en-US', {
              notation: 'compact'
            }).format(numericValue)
          }
        }
      },
      x: {
        grid: { display: false },
        border: {
          display: false
        },
        ticks: {
          color: '#524977',
          minRotation: 90,
          padding: 12,
          font: {
            size: 17,
            weight: 500
          }
        }
      }
    }
  }

  const data = useMemo(() => {
    // Single store case
    if (!multiple) {
      const single = revenues as { date: string; revenue: string }[]
      return {
        labels: single.map(item => formatDateByTimeframe(item.date, timeframe)),
        datasets: [
          {
            label: 'Revenue',
            data: single.map(item => parseFloat(item.revenue)),
            backgroundColor: '#1fcef9',
            barThickness: 22
          }
        ]
      }
    }

    const stores = revenues as {
      area: string
      revenue: { date: string; revenue: string }[]
    }[]
    const allLabels = Array.from(
      new Set(
        stores.flatMap(store =>
          (store?.revenue || []).map(item =>
            formatDateByTimeframe(item.date, timeframe)
          )
        )
      )
    )

    const colorPalette = ['#1fcef9', '#f91f6f', '#9059ff', '#ffbd4a']

    const datasets = stores.map((store, index) => {
      const color = colorPalette[index % colorPalette.length]
      const revenueMap = new Map(
        (store.revenue || []).map(item => [
          formatDateByTimeframe(item.date, timeframe),
          parseFloat(item.revenue)
        ])
      )

      return {
        label: store.area || `Store ${index + 1}`,
        data: allLabels.map(label => revenueMap.get(label) || 0),
        backgroundColor: color,
        barThickness: 22
      }
    })

    return {
      labels: allLabels,
      datasets
    }
  }, [revenues, timeframe, multiple])

  return <Bar options={options} data={data} />
}
export default RevenueBar
